import { configureStore, createSlice } from '@reduxjs/toolkit';
import { useState } from 'react';
import { useSelector } from 'react-redux';

// GET responses are keyed by their full URL, so each pagination cursor and
// filter combination has its own cache entry. Keeping it outside individual
// screens prevents a repeat request whenever an operator navigates back.
const cacheSlice = createSlice({
  name: 'adminApiCache',
  initialState: { entries: {}, refreshVersion: 0 },
  reducers: {
    received: (state, action) => {
      const { key, data, expiresAt } = action.payload;
      state.entries[key] = { data, expiresAt };
    },
    cleared: (state) => { state.entries = {}; },
    // An explicit Refresh: drop every snapshot *and* tell the mounted page to
    // fetch again, in place, without a full browser reload.
    refreshed: (state) => { state.entries = {}; state.refreshVersion += 1; },
  },
});

export const cacheActions = cacheSlice.actions;
export const store = configureStore({ reducer: { adminApiCache: cacheSlice.reducer } });

export const cachedResponse = (path) => {
  const entry = store.getState().adminApiCache.entries[path];
  return entry && entry.expiresAt > Date.now() ? entry.data : null;
};

/**
 * Like useState, but reads an initial value from the Redux API cache when
 * available. This lets a page component mount with the data it showed before
 * it was unmounted, so tab navigation never flashes a loading state.
 *
 * The `cacheKey` can be null/undefined when the key isn't known yet — the
 * hook simply falls back to `fallback`.
 */
export const useCachedState = (cacheKey, fallback = null) => {
  return useState(() => (cacheKey ? cachedResponse(cacheKey) : null) ?? fallback);
};

/**
 * Bumps each time the operator presses Refresh. Pages include it in their
 * load-effect dependencies so the current screen refetches through the API
 * while keeping its filters, pagination, and scroll position.
 */
export const useRefreshVersion = () => useSelector((state) => state.adminApiCache.refreshVersion);
